import { getPlaceTypeConfig, formatDistance, PlaceType } from './placeUtils';
import { getImageVariants } from '../imageUtils';

interface PlacePopupData {
  id: string;
  name: string;
  type: PlaceType;
  imageUrl?: string;
  photo_1_cdn?: string;
  photo_2_cdn?: string;
  photo_3_cdn?: string;
  photo_4_cdn?: string;
  photo_5_cdn?: string;
  distance?: number;
  rating?: number;
  city?: string;
  tags?: string[];
  description?: string;
  inTrip?: boolean;
}

/**
 * Generates modern, card-style popup HTML for place markers
 * Inspired by travel app designs with image, title, location, and metadata
 */
export const generatePlacePopupHTML = (place: PlacePopupData): string => {
  const typeConfig = getPlaceTypeConfig(place.type);
  
  // Collect all available photos from Firestore (photo_1 through photo_5)
  const allPhotos = [
    place.photo_1_cdn,
    place.photo_2_cdn,
    place.photo_3_cdn,
    place.photo_4_cdn,
    place.photo_5_cdn
  ].filter(Boolean); // Remove null/undefined
  
  // Use thumb variant for carousel (optimized for popup size)
  const photos: string[] = [];
  allPhotos.forEach(photo => {
    const variants = getImageVariants(photo || null);
    // Use thumb variant for popup, fallback to original
    const photoUrl = variants.thumb || variants.original || photo;
    if (photoUrl) photos.push(photoUrl);
  });
  
  // Fallback if no photos
  if (photos.length === 0) {
    photos.push('https://via.placeholder.com/300x180?text=No+Image');
  }

  const carouselId = `carousel-${place.id}`;

  return `
    <div style="
      font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
      width: 280px;
      margin: 0;
      padding: 0;
      border-radius: 12px;
      overflow: hidden;
      background: white;
      box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    ">
      <!-- Carousel Section -->
      <div id="${carouselId}" style="
        position: relative;
        width: 100%;
        height: 180px;
        background: linear-gradient(to bottom, #f3f4f6, #e5e7eb);
        overflow: hidden;
      ">
        <!-- Images -->
        ${photos.map((photo, index) => `
          <img
            class="carousel-img-${carouselId}"
            src="${photo}"
            alt="${place.name}"
            style="
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: ${index === 0 ? 'block' : 'none'};
              transition: opacity 0.3s ease;
            "
            onerror="this.style.display='none';"
          />
        `).join('')}
        
        <!-- Type Badge -->
        <div style="
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          color: #1f2937;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          display: flex;
          align-items: center;
          gap: 6px;
          z-index: 2;
        ">
          <span style="
            display: inline-block;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            background: ${getTypeColor(place.type)};
            color: white;
            font-size: 10px;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 1;
          ">${typeConfig.code}</span>
          ${typeConfig.label}
        </div>

        ${photos.length > 1 ? `
          <!-- Left Arrow -->
          <button
            class="carousel-btn-prev"
            data-carousel-id="${carouselId}"
            data-total="${photos.length}"
            style="
              position: absolute;
              top: 50%;
              left: 8px;
              transform: translateY(-50%);
              width: 32px;
              height: 32px;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.9);
              border: none;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              z-index: 2;
              transition: all 0.2s ease;
            "
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>

          <!-- Right Arrow -->
          <button
            class="carousel-btn-next"
            data-carousel-id="${carouselId}"
            data-total="${photos.length}"
            style="
              position: absolute;
              top: 50%;
              right: 8px;
              transform: translateY(-50%);
              width: 32px;
              height: 32px;
              border-radius: 50%;
              background: rgba(255, 255, 255, 0.9);
              border: none;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 2px 8px rgba(0,0,0,0.2);
              z-index: 2;
              transition: all 0.2s ease;
            "
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>

          <!-- Dots Indicator -->
          <div style="
            position: absolute;
            bottom: 12px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 6px;
            z-index: 2;
          ">
            ${photos.map((_, index) => `
              <div
                class="carousel-dot"
                data-carousel-id="${carouselId}"
                data-index="${index}"
                data-total="${photos.length}"
                style="
                  width: ${index === 0 ? '24px' : '8px'};
                  height: 8px;
                  border-radius: 4px;
                  background: ${index === 0 ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.5)'};
                  cursor: pointer;
                  transition: all 0.3s ease;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
                "
              ></div>
            `).join('')}
          </div>
        ` : ''}
      </div>

      <!-- Content Section -->
      <div style="padding: 16px;">
        <!-- Title and Location Row -->
        <div style="
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 8px;
        ">
          <!-- Left: Title and City -->
          <div style="flex: 1; min-width: 0;">
            <h3 style="
              margin: 0 0 4px 0;
              font-size: 16px;
              font-weight: 700;
              color: #111827;
              line-height: 1.3;
              letter-spacing: -0.01em;
            ">${place.name}</h3>
            
            ${place.city ? `
              <div style="
                display: flex;
                align-items: center;
                gap: 4px;
                color: #6b7280;
                font-size: 12px;
              ">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span>${place.city}</span>
              </div>
            ` : ''}
          </div>
          
          <!-- Right: Distance and Rating -->
          <div style="
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: 4px;
            flex-shrink: 0;
          ">
            ${place.distance !== undefined ? `
              <div style="
                display: flex;
                align-items: center;
                gap: 4px;
                color: #374151;
                font-size: 12px;
                font-weight: 600;
                background: #f3f4f6;
                padding: 4px 8px;
                border-radius: 6px;
              ">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 12h18"/>
                  <path d="m15 6 6 6-6 6"/>
                </svg>
                ${formatDistance(place.distance)}
              </div>
            ` : ''}
            
            ${place.rating && typeof place.rating === 'number' ? `
              <div style="
                display: flex;
                align-items: center;
                gap: 3px;
                color: #374151;
                font-size: 13px;
                font-weight: 600;
                background: #fef3c7;
                padding: 4px 8px;
                border-radius: 6px;
              ">
                <span style="color: #f59e0b; font-size: 13px;">★</span>
                ${place.rating.toFixed(1)}
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Tags -->
        ${place.tags && Array.isArray(place.tags) && place.tags.length > 0 ? `
          <div style="
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px solid #f3f4f6;
          ">
            ${place.tags.slice(0, 4).map((tag: string) => `
              <span style="
                background: #eff6ff;
                color: #1e40af;
                padding: 4px 10px;
                border-radius: 6px;
                font-size: 11px;
                font-weight: 500;
              ">${tag}</span>
            `).join('')}
          </div>
        ` : ''}
        
        <!-- Add to Trip Button -->
        <button
          class="add-to-trip-btn"
          data-place-id="${place.id}"
          style="
            width: 100%;
            margin-top: 12px;
            padding: 10px 16px;
            background: ${place.inTrip ? '#10b981' : '#3b82f6'};
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
          "
          onmouseover="this.style.background='${place.inTrip ? '#059669' : '#2563eb'}'"
          onmouseout="this.style.background='${place.inTrip ? '#10b981' : '#3b82f6'}'"
        >
          ${place.inTrip ? `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Added to Trip
          ` : `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add to Trip
          `}
        </button>
      </div>
    </div>
  `;
};

/**
 * Helper to get type-specific colors
 */
function getTypeColor(type: PlaceType): string {
  switch (type) {
    case 'attraction':
      return '#3b82f6'; // blue
    case 'food':
      return '#f97316'; // orange
    case 'experience':
      return '#8b5cf6'; // purple
    case 'hotel':
      return '#10b981'; // green
    default:
      return '#6b7280'; // gray
  }
}

